import { FC, useState, useEffect, useRef } from "react";
import {
  Header,
  Icon,
  Button,
  Typography,
  TypographyVariant,
  Checkbox,
  useCustomNavigate,
} from "@onmo/ui";
import { map } from "lodash-es";
import { useTranslation } from "react-i18next";
import { changeLanguage } from "i18next";
import { useMutation } from "@apollo/client/react/hooks/useMutation";
import { useLazyQuery } from "@apollo/client/react/hooks/useLazyQuery";
import { useQuery } from "@apollo/client/react/hooks/useQuery";
import { Query } from "@onmo/graphql/graphql";
import { updateUserGQL, meGQL } from "../../graphql";
import { getLanguages } from "../../models/settings";
import { setNotificationDefault } from "../../slices/alert";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { PageRoutes } from "../../constants/routes";
import { useStyles } from "./Languages.styles";

export const Languages: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { navigate, handleBack, setStorageData } = useCustomNavigate();
  const isFirst = useRef(true);

  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const [updateUserMutation] = useMutation(updateUserGQL);
  const [fetchUser] = useLazyQuery(meGQL);
  const { data: dataMe } = useQuery<{ me: Query["me"] }>(meGQL);
  const [lngSelect, setLngSelect] = useState(dataMe?.me?.language);
  const [isLoading, setIsLoading] = useState(false);
  const { languages } = getLanguages();
  const alert = useAppSelector((state) => state.alert);
  const regionName = new Intl.DisplayNames(i18n.language, {
    type: "language",
  });

  const onUpdateLanguage = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      changeLanguage(lngSelect);
      const lang = lngSelect ?? "en";
      localStorage.setItem("app_language", lang);
      await updateUserMutation({
        variables: {
          input: {
            language: lngSelect,
          },
        },
      });
      await fetchUser({ fetchPolicy: "network-only" });
      dispatch(setNotificationDefault(t("language_change_success")));
      setStorageData(["/"]);
      isFirst.current = false;
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setLngSelect(dataMe?.me?.language);
  }, [dataMe?.me?.language]);

  useEffect(() => {
    if (alert.isShowAlert) return;
    if (isFirst.current) {
      return;
    }
    navigate(PageRoutes.Settings);
  }, [alert.isShowAlert]);

  return (
    <div>
      <Header
        title={t("language")}
        leftElement={
          <Icon name="icon-bold-arrow-left-3" onClick={() => handleBack()} />
        }
      />
      <div className={classes.content}>
        <Typography variant={TypographyVariant.HeadingH6Bold}>
          {t("choose_preferred_language")}
        </Typography>
        <div className={classes.languages}>
          {map(languages, (lng, idx) => (
            <div
              key={idx}
              className={classes.card}
              onClick={() => setLngSelect(lng)}
            >
              <Typography variant={TypographyVariant.BodyB4Regular}>
                {lng.includes("en") ? t("english") : regionName.of(lng) || ""}
              </Typography>
              <Checkbox type="check-circle" checked={lngSelect === lng} />
            </div>
          ))}
        </div>
      </div>
      <div className={classes.bottom}>
        <Button
          variant="contained"
          disabled={languages.length === 1 || dataMe?.me?.language === lngSelect}
          fullWidth
          onClick={onUpdateLanguage}
          loading={isLoading}
        >
          {t("update")}
        </Button>
      </div>
    </div>
  );
};
